/*
 * File Path: client/src/api/authService.js
 * Description: Service functions for authentication related API calls (login, register, logout, activation, password reset).
 * Developed by: M. YOUCEF Yazid (yazid.youcef@gmail.com)
 * BDAO-SIS Project
 * Version: 1.0.0
 * CreateDate: 2025-05-01
 * UpdateDate: 2025-05-01
 */

import { apiClient, handleApiError } from './apiClient';

/**
 * Attempts to log in a user.
 * @param {object} credentials - { username, password }
 * @returns {Promise<object>} - Backend response on success (e.g., { message, userId })
 * @throws {Error} - Custom error with translated message on failure.
 */
export const login = async (credentials) => {
    try {
        // Backend response is {"message":"Login successful.","userId":3} - no token
        const response = await apiClient.post('/auth/login', credentials);
        return response.data; // Contains { message, userId }
    } catch (error) {
        handleApiError(error); // Throw translated/custom error
    }
};

/**
 * Registers a new user.
 * @param {object} registrationData - { username, email?, phone_number?, password, language }
 * @returns {Promise<object>} - Backend response on success (e.g., { userId, message })
 * @throws {Error} - Custom error with translated message on failure.
 */
export const register = async (registrationData) => {
    try {
        const response = await apiClient.post('/auth/register', registrationData);
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

/**
 * Activates a user account using the provided code.
 * @param {number|string} userId - The ID of the user to activate.
 * @param {string} code - The 6-digit activation code.
 * @returns {Promise<object>} - Backend response on success (e.g., { message })
 * @throws {Error} - Custom error with translated message on failure.
 */
export const activateAccount = async (userId, code) => {
    try {
        const response = await apiClient.post('/auth/activate', { userId, code });
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

/**
 * Resends the activation code for a given user.
 * @param {number|string} userId - The ID of the user.
 * @param {string} language - The preferred language for the email/SMS.
 * @returns {Promise<object>} - Backend response on success (e.g., { message, expiry })
 * @throws {Error} - Custom error with translated message on failure.
 */
export const resendActivationCode = async (userId, language) => {
    try {
        const response = await apiClient.post('/auth/resend-code', { userId, language });
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

/**
 * Initiates the password reset process for a user.
 * @param {string} username - The user's email or phone number.
 * @param {string} language - The preferred language for the reset email/SMS.
 * @returns {Promise<object>} - Backend response on success (e.g., { message })
 * @throws {Error} - Custom error with translated message on failure.
 */
export const forgotPassword = async (username, language) => {
    try {
       const response = await apiClient.post('/auth/forgot-password', { username, language });
       return response.data;
   } catch (error) {
       handleApiError(error);
   }
};

/**
 * Resets the user's password using a valid token.
 * @param {string} token - The password reset token.
 * @param {string} password - The new password.
 * @returns {Promise<object>} - Backend response on success (e.g., { message })
 * @throws {Error} - Custom error with translated message on failure.
 */
export const resetPassword = async (token, password) => {
    try {
        const response = await apiClient.post('/auth/reset-password', { token, password });
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

/**
 * Checks the activation status of a user account.
 * @param {number|string} userId - The ID of the user.
 * @returns {Promise<object>} - Backend response (e.g., { status: 'active' | 'deactivated' })
 * @throws {Error} - Custom error with translated message on failure.
 */
export const checkUserStatus = async (userId) => {
    try {
        // Note: This might not need handleApiError if 404 is expected
        const response = await apiClient.get(`/auth/status/${userId}`);
        return response.data;
    } catch (error) {
        // Decide if 404 should be handled differently here or let handleApiError manage it
        handleApiError(error);
    }
};

/**
 * Fetches the current logged-in user's data based on the session cookie.
 * @returns {Promise<object>} - User data object { uid, username, email, phone_number }
 * @throws {Error} - Custom error (likely 401 if not logged in, handled by interceptor/handleApiError).
 */
export const fetchUserData = async () => {
    try {
        const response = await apiClient.get('/auth/me');
        return response.data;
    } catch (error) {
        // Error (e.g., 401) is handled by the interceptor/handleApiError
        handleApiError(error); // This will re-throw the appropriate error
    }
};

/**
 * Logs the current user out by calling the backend logout endpoint.
 * @returns {Promise<object>} - Backend response on success (e.g., { message })
 * @throws {Error} - Custom error with translated message on failure.
 */
export const logout = async () => {
    try {
        const response = await apiClient.post('/auth/logout');
        return response.data;
    } catch (error) {
        handleApiError(error);
    }
};

/**
 * Verifies if the current session cookie is valid on the backend.
 * Relies on the browser/HTTP client sending the session cookie automatically.
 * @returns {Promise<object>} Resolves with { message: "Session is valid.", userId: ... } on success.
 * @throws {Error} Throws an error on failure (e.g., 401).
 */
export const verifySession = async () => {
    try {
        // The 'authenticate' middleware on the backend handles validation
        const response = await apiClient.get('/auth/verify-session');
        return response.data;
    } catch (error) {
        // Errors (like 401) will be caught by the interceptor/handleApiError
        handleApiError(error);
    }
};